import React, { useState, useEffect } from "react";
import { useTranslation } from "../../contexts/TranslationContext";
import "../../assets/css/retailFilter.scss";

import MultiSelection from "../UC/MultiSelection";
import api from "../../utils/api";

const RetailFilter = ({
    applyFilters,
    onFilterChange,
    selectionMode = false,
    className = "",
    onFilterReady,
    historyFilters,
    showTopResult = false,
    resetFiltersTrigger,
    defaultFilters
}) => {
    const { language } = useTranslation();
    const [makes, setMakes] = useState([]);
    const [selectedMakes, setSelectedMakes] = useState([]);

    // Catalog filters structure
    const [filters, setFilters] = useState({
        Make_IDs: [],
        ShoeTypes: [],
        Models: [],
        ProductCode: "",
        SearchFilterMode: "like",
        TopResult: 100 // Default value for top result
    });

    const [inputShoeType, setInputShoeType] = useState("");
    const [inputModels, setInputModels] = useState("");

    useEffect(() => {
        if (onFilterChange) {
            onFilterChange(filters);
        }
    }, [filters, onFilterChange]);

    useEffect(() => {
        if (resetFiltersTrigger && defaultFilters) {
            setFilters(defaultFilters);

            setSelectedMakes([]);

            setInputShoeType((defaultFilters.ShoeTypes || []).join(", "));
            setInputModels((defaultFilters.Models || []).join(", "));
        }
    }, [resetFiltersTrigger, defaultFilters]);

    // Load filters from history data when historyFilters prop changes
    useEffect(() => {
        if (!historyFilters || makes.length === 0) return;

        // Extract Make_IDs and find corresponding make objects
        let arrMake_IDs = makes.filter(make =>
            // find makes in historyFilters.Makes in lower case for case insensitive match
            historyFilters.Makes.map(name => name.toLowerCase()).includes(make.Value.toLowerCase())
        );

        setSelectedMakes(arrMake_IDs);

        // Update filters state with history data
        setFilters(prevFilters => ({
            ...prevFilters,
            Make_IDs: historyFilters.Make_IDs || [],
            ShoeTypes: historyFilters.ShoeTypes || [],
            Models: historyFilters.Models || [],
            ProductCode: historyFilters.ProductCode || "",
            TopResult: historyFilters.CandidateSize || prevFilters.TopResult
        }));
    }, [historyFilters, makes]);

    // Load Makes from localStorage when component mounts
    useEffect(() => {
        // const storedMakes = localStorage.getItem("list_makes");
        // if (storedMakes) {
        //     setMakes(JSON.parse(storedMakes));
        //     // Notify parent that filter is ready (immediate for cached data)
        //     if (onFilterReady) {
        //         setTimeout(() => onFilterReady(true), 0);
        //     }
        // } else 
            //{
            const fetchMakesFromAPI = async () => {
                try {
                    if (onFilterReady)
                        onFilterReady(false);

                    const _tokenRequestAPI = localStorage.getItem("FWToken");
                    const response = await api.post(
                        `${global.config.api.domain}/fwiq-api/api/catalog/getpartialfilters`,
                        { Token: _tokenRequestAPI, Fields: ["make"] }
                    );

                    if (response.status === 200 || response.status === 204) {
                        let data = response.data;
                        // Safety: if response.data is still a string (parser failed), try JSON.parse
                        if (typeof data === 'string') {
                            try { data = JSON.parse(data); } catch { data = {}; }
                        }
                        data = data || {};
                        if (data.Makes) {
                            setMakes(data.Makes);
                        }

                        if (onFilterReady) {
                            setTimeout(() => onFilterReady(true), 0);
                        }
                    } else {
                        console.error("Failed to fetch makes from API");
                        if (onFilterReady) {
                            onFilterReady(false);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching makes:", error);
                    if (onFilterReady) {
                        onFilterReady(false);
                    }
                }
            };
            fetchMakesFromAPI();
        //}
    }, [onFilterReady]);

    const handleMakeChange = selectedOptions => {
        setSelectedMakes(selectedOptions);

        console.log("Selected Makes:", selectedOptions.map(option => option.ID));

        setFilters(prevFilters => ({
            ...prevFilters,
            Make_IDs: selectedOptions.map(option => option.ID)
        }));
    };

    const handleApplyFilters = () => {
        console.log("Applying retail catalog filters:", filters);

        let formattedFilters = {
            Make_IDs: filters.Make_IDs,
            ShoeTypes: filters.ShoeTypes,
            Models: filters.Models,
            ProductCode: filters.ProductCode,
            SearchFilterMode: "like"
        };

        console.log("Formatted filters for API:", formattedFilters);

        // Call the applyFilters function passed from parent component
        applyFilters(formattedFilters);
    };

    const shoeTypeChange = (value) => {
        setInputShoeType(value);
        const shoeTypesArray = value.split(/[,;]/).map(item => item.trim()).filter(item => item);
        setFilters(prevFilters => ({ ...prevFilters, ShoeTypes: shoeTypesArray }));
    };

    const modelsChange = (value) => {
        setInputModels(value);
        const modelsArray = value.split(/[,;]/).map(item => item.trim()).filter(item => item);
        setFilters(prevFilters => ({ ...prevFilters, Models: modelsArray }));
    };

    return (
        <div className={`galleryFilter retailFilter${selectionMode ? " selectionMode" : ""}${className ? ` ${className}` : ""}`}>

            {/* Retail catalog specific filters */}
            <div className="filters" data-type="catalog">
                {/* Make Selection */}
                <div className="a-filter">
                    <div className="filter-title">{language.galleryFilter?.make || "Make"}</div>
                    <div className="filter-data">
                        <MultiSelection
                            options={makes}
                            placeholder={language.galleryFilter?.selectMake || "Select Make"}
                            onChange={handleMakeChange}
                            defaultSelected={selectedMakes}
                        />
                    </div>
                </div>

                {/* Shoe Type filter */}
                <div className="a-filter">
                    <div className="filter-title">{language.galleryFilter?.type || "Type"}</div>
                    <div className="filter-data">
                        <input
                            type="text"
                            name="ShoeTypes"
                            value={inputShoeType}
                            onChange={e => {
                                shoeTypeChange(e.target.value);
                            }}
                            placeholder={language.galleryFilter?.enterShoeType || "Enter type(s), separated by commas or semicolons"}
                        />
                    </div>
                </div>

                {/* Model filter */}
                <div className="a-filter">
                    <div className="filter-title">{language.galleryFilter?.model || "Model"}</div>
                    <div className="filter-data">
                        <input
                            type="text"
                            name="Models"
                            value={inputModels}
                            onChange={e => {
                                modelsChange(e.target.value);
                            }}
                            placeholder={language.galleryFilter?.enterModel || "Enter model(s), separated by commas or semicolons"}
                        />
                    </div>
                </div>

                {/* Product ID filter */}
                <div className="a-filter">
                    <div className="filter-title">{language.galleryFilter?.productId || "Product ID"}</div>
                    <div className="filter-data">
                        <input
                            type="text"
                            id="txtProductCode_Catalog"
                            value={filters.ProductCode}
                            onChange={e =>
                                setFilters(prevFilters => ({ ...prevFilters, ProductCode: e.target.value }))}
                            placeholder={language.galleryFilter?.enterProductId || "Enter product ID"}
                        />
                    </div>
                </div>

                {/* Top Result filter - conditional display */}
                {showTopResult && (
                    <div className="a-filter">
                        <div className="filter-title">{language.galleryFilter?.topResult || "Top Result"}</div>
                        <div className="filter-data">
                            <input
                                type="number"
                                id="txtTopResult"
                                value={filters.TopResult}
                                onChange={e =>
                                    setFilters(prevFilters => ({ ...prevFilters, TopResult: parseInt(e.target.value) || 100 }))}
                                placeholder="100"
                                min="1"
                                max="500"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Filter actions */}
            <div className="filter-actions">
                <button className="apply-filters-btn" onClick={handleApplyFilters}>
                    <i className="fa fa-filter" />
                    {language.galleryFilter?.applyFilters || "Apply Filters"}
                </button>
            </div>

        </div>
    );
};

export default RetailFilter;
